<template>
	<div tabindex="0" @keyup.esc="closeAdd()" class="hide-outline">
		<TopPanel
			v-if="mapHasNoFile || showList"
			:units="[
			$t('floor_detail.unit_singular'),
			$t('floor_detail.unit_plural'),
			$t('floor_detail.unit_new')
			]"
			:pagination="pagination"
			:orderBys="orderBys"
			:orderBy="mapItemParams.orderBy"
			:showCompany="false"
			:hide-add="!$isAllowedTo('floor_areas_edit')"
			@toggleAdd="toggleAddMapItem"
			@search="search"
			@orderBy="orderBy"
			noSearch
		>
			<div v-if="$isAllowedTo('floor_areas_edit')" class="top-column">
				<div class="panel-block">
					<button
						class="button is-small is-info button--is-clickable"
						@click.stop="editMap(maps[0])"
						v-if="mapHasNoFile"
					>
						<span class="icon"><i class="fas fa-map"/></span>
						<span>{{ $t('floor_detail.add_drawing') }}</span>
					</button>
				</div>
			</div>
		</TopPanel>
		<main
			class="main-view"
			:class="mapHasNoFile || showList ? '' : 'no-top-panel'"
			@click="closeAdd()"
		>
			<div
				class="level main"
				v-if="!mapHasNoFile"
				:class="{ offset: isMobile }"
			>
				<div class="level-left">
					<div class="title" v-if="!showList">
						{{ title }}
					</div>
				</div>

				<div
					class="switch"
					:class="{ offset: showList && isMobile, mobile: isMobile }"
				>
					<p>{{ $t('floor_detail.show_list') }}</p>
					<switchButton v-model="showList" />
				</div>
			</div>
			
			<div v-for="map in maps" :key="map.id">
				<MapDrop
					v-if="map.file && map.file != null && !showList"
					:key="mapDropKey"
					ref="mapDropComponent"
					:floor="floor"
					:map="map"
					:fireProtections="listFireProtections"
					:leaving="leaving"
					:areas="areas"
					:items="allMapItems"
					@delete="askToDelete"
					@deleteArea="askToDeleteArea"
					@deleteMapItem="askToDeleteMapItem"
					@edit="editMap"
					@saveArea="saveArea"
					@saveMapItem="saveMapItem"
					@editMapItem="editMapItem"
					@newMapItem="newMapItem"
				/>
				<div v-else class="columns">
					<MapItemList
						:building="building"
						:map="map"
						:floor="floor"
						:items="mapItems"
						@edit-map="editMap"
						@delete-map="askToDelete"
						@edit-item="editMapItem"
						@delete-item="askToDeleteMapItem"
						@paginate="getMapItems()"
						@update="saveMapItem"
					/>
				</div>
			</div>
		</main>
		<AddMap
			v-if="showAddMap"
			@save="saveMap"
			@cancel="closeAdd"
			:editMap="selectedMap"
			:position="mapPosition"
		/>
		<AddMapItem
			v-if="showAddMapItem"
			:key="(selectedMapItem?.id || 'new') + ':' + (selectedMapItem?.updated || '')"
			:item="selectedMapItem"
			:fireProtections="listFireProtections"
			:hasMapFile="!mapHasNoFile && !showList"
			@cancel="closeAdd"
			@save="saveMapItem"
			@delete="deleteItemFromModal"
		/>
		<DeleteModal
			v-if="showDelete"
			@confirm="deleteSelected"
			@cancel="cancelDelete"
			:message="deleteMessage"
		/>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import DeleteModal from '@/components/DeleteModal';
import MapDrop from './components/MapDrop';
import AddMap from './modals/AddMap';
import AddMapItem from './modals/AddMapItem';
import MapItemList from './components/MapItemList';
import TopPanel from '@/components/TopPanel';
import switchButton from '@/components/switchButton';
export default {
	components: {
		DeleteModal,
		MapDrop,
		AddMap,
		AddMapItem,
		MapItemList,
		TopPanel,
		switchButton
	},
	data() {
		return {
			showAddMap: false,
			showDelete: false,
			showAddMapItem: false,
			selectedMap: {},
			selectedArea: {},
			selectedMapItem: {},
			mapPosition: { top: 0, left: 0 },
			leaving: false,
			showList: false
		};
	},
	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('floorStore', ['floor']),
		...mapGetters('mapStore', ['maps']),
		...mapGetters('mapItemStore', [
			'mapItems',
			'allMapItems',
			'pagination',
			'totalCount'
		]),
		orderBys() {
			return [
				{ name: this.$t('common.order_created'), value: 'Created' },
				{ name: this.$t('floor_detail.position_number'), value: 'PositionNumber' }
			];
		},
		 mapDropKey() {
			return (this.allMapItems || [])
			.map(i => `${i.id}:${new Date(i.updated || i.created || 0).getTime()}`)
			.join('|');
		},
		...mapGetters('fireProtectionStore', ['listFireProtections']),
		...mapGetters('areaStore', ['areas']),
		...mapGetters('buildingStore', ['building']),
		...mapGetters('user', ['getActiveCustomer']),
		...mapState('mapStore', {
			mapParams: 'searchParams'
		}),
		...mapState('mapItemStore', {
			mapItemParams: 'searchParams'
		}),
		...mapState('fireProtectionStore', ['fireExtinguishers']),
		deleteMessage() {
			if (this.selectedMap.name) {
				return this.$t('floor_detail.confirm_delete_map', { name: this.selectedMap.name });
			} else if (this.selectedArea.name) {
				return this.$t('floor_detail.confirm_delete_area', { name: this.selectedArea.name });
			} else {
				return this.$t('floor_detail.confirm_delete_item', {
					name: this.selectedMapItem.fireProtection.name
				});
			}
		},
		mapHasNoFile() {
			if (this.maps[0]) {
				if (this.maps[0].file) {
					return false;
				}
			}

			return true;
		},
		title() {
				return this.building.name
					? `${this.$t('floor_detail.building')}: ${this.building.name} | ${this.building.customer.name}`
					: '';
		}
	},
	watch: {
		showList(to) {
			if (to) {
				this.getMapItems();
			} else {
				this.getAllMapItems(this.maps[0].id);
			}
		},
		'getActiveCustomer.id'() {
			if (this.getActiveCustomer.id !== this.building.customerId) 
				this.$router.push(`/${this.$i18n.locale}/buildings`);
		}
	},
	methods: {
		...mapActions('fireProtectionStore', ['getListFireProtections']),
		...mapActions('floorStore', ['getFloor']),
		...mapActions('buildingStore', ['getBuilding']),
		...mapActions('mapStore', [
			'getMaps',
			'createMap',
			'updateMap',
			'deleteMap'
		]),
		...mapActions('areaStore', [
			'getAreas',
			'createArea',
			'updateArea',
			'deleteArea',
			'setAreaMapId'
		]),
		...mapActions('mapItemStore', [
			'getMapItems',
			'getAllMapItems',
			'createMapItem',
			'updateMapItem',
			'deleteMapItem',
			'setMapItemMapId'
		]),
		...mapActions('fileStore', ['saveFile', 'getFile']),
		toggleAdd() {
			if (!this.selectedMap.name) {
				this.showAddMap = !this.showAddMap;
			}

			this.selectedMap = {};
			this.mapPosition = { top: 0, left: 0 };
		},
		toggleAddMapItem() {
			if (!this.showAddMapItem) this.setZoom();

			if (!this.selectedMapItem.fireProtection) {
				this.showAddMapItem = !this.showAddMapItem;
			}

			this.selectedMapItem = {};
		},
		closeAdd() {
			if (window.getSelection().toString() == '') {
			this.showAddMap = false;
			this.selectedMap = {};
			}
			this.showAddMapItem = false;
			this.selectedMapItem = { model: '', fireProtection: {}, map: {} };
			this.showDelete = false;
		},
		setZoom() {
			var viewport = document.querySelector('meta[name="viewport"]');

			if (viewport) {
				viewport.content = 'initial-scale=1';
				viewport.content = `width=${window.innerWidth}`;
			}
		},
		saveMap(map) {
			map.floorId = this.floor.id;

			if (this.selectedMap.id) {
				this.updateMap(map).then(() => {
					this.showAddMap = false;
					this.selectedMap = {};
				});
			} else {
				this.createMap(map).then(() => {
					this.showAddMap = false;
				});
			}
		},
		editMap(map) {
			if (this.selectedMap == map) {
				this.selectedMap = {};
				this.showAddMap = false;
			} else {
				this.selectedMap = map;
				this.showAddMap = true;
			}
		},
		askToDelete(map) {
			this.showAddMap = false;
			Object.assign(this.selectedMap, map);
			this.showDelete = true;
		},
		askToDeleteArea(area) {
			this.showAddMap = false;
			Object.assign(this.selectedArea, area);
			this.showDelete = true;
		},
		askToDeleteMapItem(mapItem) {
			this.showAddMap = false;
			Object.assign(this.selectedMapItem, mapItem);
			this.showDelete = true;
		},
		cancelDelete() {
			this.showDelete = false;
			this.selectedMap = {};
		},
		deleteSelected() {
			if (this.selectedMap.name) {
				this.deleteMap(this.selectedMap).then(() => {
					this.selectedMap = {};
				});
			}

			if (this.selectedArea.name) {
				this.deleteArea(this.selectedArea).then(() => {
					this.selectedArea = {};
				});
			}

			if (this.selectedMapItem.fireProtection) {
				this.deleteMapItem(this.selectedMapItem).then(() => {
					this.selectedMapItem = {};
					this.getMapItems();

					if (!this.mapHasNoFile) {
						this.getAllMapItems(this.maps[0].id);
					}
				});
			}

			this.showDelete = false;
		},
		saveArea(area) {
			if (area.id) {
				this.updateArea(area);
			} else {
				this.createArea(area);
			}
		},
		normalizeMapItemForApi(input) {
			const clone = JSON.parse(JSON.stringify(input)); 

			const cleanEmpties = obj => {
				if (obj && typeof obj === 'object') {
				Object.keys(obj).forEach(k => {
					const v = obj[k];
					if (v === '' || v === null) delete obj[k];
					else if (typeof v === 'object') cleanEmpties(v);
				});
				}
			};

			const keepIntYears = new Set([
				'FusibleLinkYear','ChargeYear','RevisionYear','InstallationYear',
				'TestPressureYear','GasspringRevisionYear'
			]);

			const coerceDates = obj => {
				if (!obj || typeof obj !== 'object') return;

				for (const [k, v] of Object.entries(obj)) {
				if (v && typeof v === 'object') {
					coerceDates(v);
					continue;
				}

				if (keepIntYears.has(k)) {
					if (typeof v === 'string' && /^\d{2,4}$/.test(v)) obj[k] = parseInt(v, 10);
					continue;
				}

				if (k === 'year') {
					if (typeof v === 'number' || (typeof v === 'string' && /^\d{2,4}$/.test(v))) {
					let yyyy = typeof v === 'number' ? v : parseInt(v, 10);
					if (yyyy < 100) yyyy += 2000; 
					obj[k] = `${yyyy}-01-01`; 
					}
				}
				}
			};

			cleanEmpties(clone);
			coerceDates(clone);

			if (clone.fireProtection && clone.fireProtection.id && !clone.fireProtectionId) {
				clone.fireProtectionId = clone.fireProtection.id;
			}
			if (clone.map && clone.map.id && !clone.mapId) {
				clone.mapId = clone.map.id;
			}

			delete clone.fireProtection;
			delete clone.map;

			return clone;
		},
		async saveMapItem(mapItem) {
			if (mapItem.id) {
				mapItem.fireProtectionId = mapItem.fireProtection?.id || mapItem.fireProtectionId;
				mapItem.mapId = mapItem.map?.id || mapItem.mapId;
			} else {
				if (!mapItem.mapId) {
				mapItem.mapId = this.maps[0].id;
				if (!mapItem.positionX || !mapItem.positionY) {
					mapItem.positionX = 50;
					mapItem.positionY = 50;
				}
				mapItem.scale = 1;
				mapItem.rotation = 0;
				if (!mapItem.positionNumber) mapItem.positionNumber = this.totalCount + 1;
				}
			}

			const payload = this.normalizeMapItemForApi(mapItem);

			if (mapItem.id) {
				this.updateMapItem(payload).then(() => {
				this.showAddMapItem = false;
				this.getMapItems();
				if (!this.mapHasNoFile) this.getAllMapItems(this.maps[0].id);
				});
			} else {
				if (!this.mapHasNoFile) {
				const responseItem = await this.createMapItem(payload);
				this.selectedMapItem = responseItem;
				this.showAddMapItem = !this.showList;
				if (!this.mapHasNoFile) this.getAllMapItems(this.maps[0].id);
				} else {
					this.createMapItem(payload).then(() => {
						this.showAddMapItem = false;
					});
				}
			}
		}
		,
		editMapItem(mapItem) {
			this.setZoom();
			this.selectedMapItem = mapItem;
			this.showAddMapItem = true;
		},
		newMapItem(mapItem) {
			console.log("newMapItem", mapItem);
			this.setZoom();
			mapItem.mapId = this.maps[0].id;
			mapItem.scale = 1;
			mapItem.rotation = 0;
			mapItem.positionNumber = this.totalCount + 1;
			this.selectedMapItem = mapItem;
			this.showAddMapItem = true;
		},
		search(searchValue) {
			this.mapItemParams.name = searchValue;
			this.getMapItems();
		},
		orderBy(orderBy) {
			this.mapItemParams.orderBy = orderBy;
			this.getMapItems();
		},
		deleteItemFromModal(item) {
			this.showAddMapItem = false;

			if (this.$refs.mapDropComponent)
				this.$refs.mapDropComponent[0].toggleMenu();

			this.askToDeleteMapItem(item);
		},
		
	},
	async created() {
		this.setZoom();
		this.getFloor(this.$route.params.floorId);
		this.mapParams.floorId = this.$route.params.floorId;
		this.getListFireProtections();
		await this.getMaps().then(() => {
			if (this.maps.length > 0) {
				this.setMapItemMapId(this.maps[0].id);
				this.setAreaMapId(this.maps[0].id);
				this.getAreas();
				this.getMapItems();
				this.getAllMapItems(this.maps[0].id);
				this.getBuilding(this.maps[0].floor.buildingId);
			}
		});
	}
};
</script>

<style lang="scss" scoped>
.level.main {
  margin-bottom: 0.5rem !important;
  &.offset {
    margin-top: 2rem;
  }
}
.switch {
  display: flex;
  right: 0.5rem;
  top: 3rem;
  padding-left: 5px;
  padding-top: 1px;

  &.mobile {
    margin-right: 0.25rem;
    position: fixed;
  }

  &.offset {
    top: 5.7rem;
    &.mobile {
      background: #ffffffe5;
      box-shadow: -5px 5px 10px #ffffffe5;
      z-index: 4;
      top: 3rem;
    }
  }
  p {
    margin-top: 0.6rem;
    margin-right: 0.2rem;
    font-size: 14px;
  }
}
</style>
